import { Injectable } from '@angular/core';
import { parentMessages$, publishParentMessage } from '../security/EmbedMessageBus';
import { Observable, merge, EMPTY } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { VBrickPlayerComponent } from '@vbrick/vbrick-player/app/player/VbrickPlayer.Component';
import { VideogularComponent } from '@vbrick/vbrick-player/app/player/videogular/Videogular.Component';
import { PlayerStatus, SDK_CC_LANG_SPECIFIER, ISDKVideoInfo, SDKPlayerMethod } from './ISDKPlayerContracts';
import { SDKEventHandler, ISDKMethodHandlers } from './ISDKContracts';
import { clamp } from '../util';


@Injectable({
	providedIn: 'root'
})
export class EmbedPlayerMessageBus {

	public onVodLoaded(video: any) {
		publishParentMessage('videoLoaded', this.formatVideoLoadedInfo(video));
	}

	private formatVideoLoadedInfo(video: any): ISDKVideoInfo {
		return {
			videoId: video.id,
			status: video.status,
			title: video.title,
			duration: video.duration ? video.duration / 1000 : video.duration,
			isLive: video.isLive,
			is360: video.is360,
			hasAudioOnly: video.hasAudioOnly,
			subtitles: [
				video.closedCaptionsEnabled ? { language: SDK_CC_LANG_SPECIFIER } : undefined,
				...video.subtitles.map(({ languageCode }) => ({ language: languageCode }))
			].filter(Boolean),
			chapters: video.chapterInfo.chapters.map(chapter => ({
				imageId: chapter.imageId,
				extension: chapter.extension,
				time: chapter.time / 1000,
				title: chapter.title
			})),
			playbacks: video.playbacks?.map(playback => ({
				id: playback.videoInstanceId,
				label: playback.label,
				streamDeliveryType: playback.streamDeliveryType,
				zoneName: playback.zoneName,
				deviceName: playback.deviceName
			}))
		};
	}

	public handlePlayerEvents(player: VBrickPlayerComponent, vgApi: VideogularComponent): Observable<any> {
		const parentMessageHandlers: ISDKMethodHandlers<SDKPlayerMethod> = {
			playVideo: () => vgApi.play(),
			pauseVideo: () => vgApi.isLive ? vgApi.stop() : vgApi.pause(),
			setPlaybackSpeed: ({ msg }) => {
				const speed = clamp(msg?.speed, 0, 16);
				if(isFinite(speed)) {
					vgApi.setPlayback(msg.speed);
				}
			},
			setVolume: ({ msg }) => {
				const volume = clamp(msg?.volume, 0, 1);
				if(isFinite(volume)) {
					vgApi.setVolume(volume);
				}
			},
			setSubtitles: ({ msg }) => {
				// turn off captions if enabled: false
				if(!msg?.enabled) {
					player.subtitleMenuComp.onManualLangChange(null);
					return;
				}
				const option = player.subtitleOptions?.find(o => o.language === msg.language);
				// if not found then assume Close Captions is desired
				player.subtitleMenuComp.onManualLangChange(option || player.subtitleMenuComp.SUBTITLE_OPTION_CAPTIONS);
			},
			seek: ({ msg }) => {
				const time = clamp(msg?.currentTime, 0, player.totalTimeWithFallback);
				if(isFinite(time)) {
					vgApi.seekTime(msg.currentTime);
				}
			}
		};

		/**
		 * This is a mapping of observers for player events and the corresponding SDK event + event payload
		 * To add additional handlers include the corresponding definition into TSDKEvents, currently
		 * defined in ./ISDKPlayerContracts.ts, but may be moved to the public @vbrick/rev-sdk library in the
		 * future.
		 * Example:
		 *   in here: [MyObservable, () => ['eventName', { event: 'payload' }]]
		 *   in TSDKPlayerMessage: ['eventName', { event: string }]
		 */
		const eventHandlers: Array<[Observable<any>, SDKEventHandler]> = [
			[vgApi.vgVolumeChanged.pipe(
				map(e => e.volume),
				distinctUntilChanged()
			), volume => ['volumeChanged', volume]],
			[vgApi.vgActiveSubtitleChange, ({ subtitle }) => [
				'subtitlesChanged', {
					language: subtitle?.language,
					enabled: !!subtitle
				}
			]],
			[vgApi.vgPlaybackRateChanged.pipe(
				map(e => e.playbackRate),
				distinctUntilChanged()
			), speed => ['playbackSpeedChanged', speed]],
			[player.onPlaybackStarted, () => ['playerStatusChanged', { status: PlayerStatus.Playing }]],
			[player.onPlay, () => ['playerStatusChanged', { status: PlayerStatus.Playing }]],
			[player.onPause, () => ['playerStatusChanged', { status: PlayerStatus.Paused }]],
			[player.onStop, () => ['playerStatusChanged', { status: PlayerStatus.Paused }]],
			[player.onBufferingStart, () => ['playerStatusChanged', { status: PlayerStatus.Buffering }]],
			[player.onBufferingStop, () => ['playerStatusChanged', { status: PlayerStatus.Playing }]],
			[player.onSeek, e => ['seeked', e]],
			[player.onComplete, () => ['playerStatusChanged', { status: PlayerStatus.Ended }]],
			[vgApi.currentTime$, (currentTime: number) => [
				'currentTime', {
					currentTime: currentTime / 1000,
					duration: player.totalTimeWithFallback
				}
			]]
		];

		return merge(
			parentMessages$.pipe(
				tap(e => {
					parentMessageHandlers[e.event]?.(e);
				}),
				catchError(error => EMPTY)
			),
			...eventHandlers.map(([source, handler]) => (source as Observable<any>).pipe(
				map(handler as any),
				tap(([type, msg]) => publishParentMessage(type, msg))
			))
		);
	}
}
